import React, { useEffect, useState } from "react";
import { AspectRatio, Box, type BoxProps, type LayoutProps, type SpaceProps } from "@chakra-ui/react";
import { isSafari } from "react-device-detect";
import { type BrandColorToken } from "theme/colors";
import { Lottie, type LottieProps } from "./lottie";

type LottieWithSafariVideoProps = {
  /**
   * The video source to use instead of lottie in Safari.
   */
  videoSrc: string;

  /**
   * Whether the video is exported with H.265 alpha transparency.
   */
  isVideoTransparent?: boolean;

  /**
   * The border radius for the video.
   */
  videoBorderRadius?: BoxProps["borderRadius"];

  /**
   * Placeholder to show while hydration occurs.
   *
   * Defaults to a rounded box with the same aspect ratio as the animation.
   */
  placeholder?: JSX.Element;

  /**
   * Background color for the placeholder while hydration occurs.
   *
   * @default "new.gray.50"
   */
  placeholderBgColor?: BrandColorToken | "transparent";

  /**
   * Optional props to add to the `AspectRatio` that wraps the video.
   */
  staticContainerProps?: LayoutProps & SpaceProps;
} & LottieProps;

/**
 * A Lottie animation component that will alternatively render an MP4 video in Safari only.
 * Use this when a Lottie animation does not play nicely in Safari (e.g. has lag).
 */
export function LottieWithSafariVideo({
  isVideoTransparent,
  videoSrc,
  videoBorderRadius = { base: "small", md: "bigger" },
  placeholder,
  placeholderBgColor = "new.gray.50",
  staticContainerProps,
  ratio,
  ...lottieProps
}: LottieWithSafariVideoProps) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Resolve hydration issue and render a placeholder during client-side rendering
  if (!isClient) {
    return (
      placeholder ?? (
        <AspectRatio ratio={ratio} w="100%" borderRadius={videoBorderRadius} overflow="hidden">
          <Box bg={placeholderBgColor} boxSize="100%" />
        </AspectRatio>
      )
    );
  }

  if (isSafari) {
    return (
      <AspectRatio ratio={ratio} w="100%" borderRadius={videoBorderRadius} overflow="hidden" {...staticContainerProps}>
        <video autoPlay loop muted playsInline width="100%" height="100%" preload="metadata">
          <source src={videoSrc} type={`video/mp4${isVideoTransparent ? ";codecs=hvc1" : ""}`} />
        </video>
      </AspectRatio>
    );
  }

  return <Lottie ratio={ratio} {...lottieProps} />;
}
